import { getMediaUrl, initialDieTypeQuantities, RollTos } from "@/constants";
import { Context } from "@/context";
import { rollDice } from "@/helpers/rollDice";
import { useDiceRollPrivacyStore } from "@/hooks/useDiceRollPrivacyStore";
import { useShared3dDiceSettingStore } from "@/hooks/useShared3dDiceSettingStore";
import type { DieTypeQuantities } from "@/types";
import ArrowUpRightFromSquare from "@dndbeyond/fontawesome-cache/svgs/light/arrow-up-right-from-square.svg";
import D10 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d10.svg";
import D12 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d12.svg";
import D20 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d20.svg";
import D4 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d4.svg";
import D6 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d6.svg";
import D8 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d8.svg";
import Plus from "@dndbeyond/fontawesome-cache/svgs/solid/plus.svg";
import User from "@dndbeyond/fontawesome-cache/svgs/solid/user.svg";
import Users from "@dndbeyond/fontawesome-cache/svgs/solid/users.svg";
import { Button } from "@dndbeyond/ttui/components/Button";
import { DismissableSection } from "@dndbeyond/ttui/components/DismissableSection";
import { ToggleSwitch } from "@dndbeyond/ttui/components/ToggleSwitch";
import { Tooltip } from "@dndbeyond/ttui/components/Tooltip";
import clsx from "clsx";
import {
  useContext,
  useEffect,
  useState,
  type FC,
  type MouseEvent,
  type MouseEventHandler,
} from "react";
import type { CustomDiceRollerProps } from "./CustomDiceRoller";
import { D100 } from "./D100";
import styles from "./styles.module.css";

const Icons: { [key: string]: FC } = {
  d4: D4,
  d6: D6,
  d8: D8,
  d10: D10,
  d12: D12,
  d20: D20,
  d100: D100,
};
const DieIcon: FC<{
  notation: string;
  quantity: number;
  increment: MouseEventHandler<HTMLButtonElement>;
  decrement: MouseEventHandler<HTMLButtonElement>;
}> = ({ notation, quantity, increment, decrement }) => {
  const Icon = Icons[notation];

  return (
    <>
      <Button
        className={clsx([styles.die, styles[notation]])}
        data-quantity={quantity > 0 ? quantity : undefined}
        onClick={increment}
        onContextMenu={decrement}
        id={notation}
        data-testid={notation}
        data-tooltip-id={notation}
      >
        <Icon />
        <Plus className={styles.plus} />
        <span>{notation}</span>
      </Button>
      <Tooltip id={notation}>{quantity === 0 && `Add ${notation}`}</Tooltip>
    </>
  );
};

export const Roller: FC<CustomDiceRollerProps> = ({
  onClose,
  rollInstrumentationCallback,
  rollCallback,
  rollErrorCallback,
  canUseShared3dDice = false,
  userId,
  setId,
  clearDiceCallback,
  rollToCallback,
  diceSets,
  gameId,
  preview,
  customDiceSet,
  entityType = "user",
  entityId = String(userId),
  messageTarget = userId,
  name,
  ...rest
}) => {
  const { nodeEnv, env } = useContext(Context);
  const [diceToRoll, setDiceToRoll] = useState<DieTypeQuantities>(
    initialDieTypeQuantities,
  );
  const [isOnline, setIsOnline] = useState(globalThis.navigator?.onLine);
  const { getIsShared3dDiceSettingEnabled, setIsShared3dDiceSettingEnabled } =
    useShared3dDiceSettingStore();
  const isShared3dDiceEnabled = getIsShared3dDiceSettingEnabled(
    canUseShared3dDice,
    userId,
  );
  const { getDiceRollPrivacy, setRollToPrivacy } = useDiceRollPrivacyStore();
  const rollTo = getDiceRollPrivacy(userId)?.rollTo;
  const incrementDie = (e: MouseEvent<HTMLButtonElement>) =>
    setDiceToRoll({
      ...diceToRoll,
      [e.currentTarget.id]:
        diceToRoll[e.currentTarget.id as keyof DieTypeQuantities] + 1,
    });
  const decrementDie = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const die = e.currentTarget.id as keyof DieTypeQuantities;

    if (diceToRoll[die] === 0) return;

    setDiceToRoll({
      ...diceToRoll,
      [e.currentTarget.id]: Math.max(0, diceToRoll[die] - 1),
    });
  };
  const emptyDiceToRoll = Object.values(diceToRoll).every((i) => i === 0);
  const handleRoll = () => {
    setTimeout(() => {
      try {
        const payload = rollDice(
          {
            diceNotation: Object.entries(diceToRoll)
              .filter(([, quantity]) => quantity > 0)
              .map(([d, quantity]) => `${quantity}${d}`)
              .join("+"),
            action: "custom",
            rollType: "roll",
            entityId,
            entityType,
            userId: String(userId),
            setId: setId!,
            gameId: String(gameId),
            isShared3dDiceEnabled,
            messageTarget,
            name,
          },
          (instrumentationPayload) =>
            rollInstrumentationCallback?.(instrumentationPayload),
        );

        rollCallback?.(payload);
      } catch (exception) {
        rollErrorCallback?.(exception);
      }

      setDiceToRoll(initialDieTypeQuantities);
    });
  };
  const handleRollTo = (e: MouseEvent<HTMLButtonElement>) => {
    if (rollTo !== e.currentTarget.id) {
      setRollToPrivacy(userId);
    }

    rollToCallback?.(e);
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    globalThis.addEventListener("online", handleOnline);
    globalThis.addEventListener("offline", handleOffline);

    return () => {
      globalThis.removeEventListener("online", handleOnline);
      globalThis.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <DismissableSection heading="Roll Dice" onClose={onClose} {...rest}>
      <div className={styles.wrapper}>
        <div className={styles.shared3dDiceContainer}>
          <div className={styles.shared3dDiceImageWrapper}>
            {!customDiceSet && (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className={clsx([
                    styles.shared3dDiceImage,
                    !isShared3dDiceEnabled && styles.shared3dDiceImageDisabled,
                  ])}
                  src={`${getMediaUrl(nodeEnv!, env!)}/dice/images/thumbnails/${setId}-d20-20.png`}
                  alt="Current Dice Set"
                  width={128}
                  height={128}
                />
              </>
            )}
          </div>
          <div className={styles.diceTextContainer}>
            <p className={styles.diceSetLabel}>
              {isShared3dDiceEnabled
                ? customDiceSet
                  ? "Custom Dice Set"
                  : diceSets?.find(({ id }) => id === setId)?.name
                : "3D Dice Disabled"}
            </p>
            {!preview && isOnline && (
              <a
                href={`https://${nodeEnv === "development" ? "stg" : "www"}.dndbeyond.com/my-dice`}
                className={styles.changeDiceSetLink}
                target="_blank"
              >
                Change Dice <ArrowUpRightFromSquare />
              </a>
            )}
          </div>
          <div className={styles.toggleSwitchContainer}>
            <ToggleSwitch
              id="shared3dDiceToggleSwitch"
              isOn={isShared3dDiceEnabled}
              label=""
              onToggle={() => setIsShared3dDiceSettingEnabled(userId)}
            />
          </div>
        </div>
        <div className={styles.diceContainer}>
          <div className={styles.dice}>
            {Object.keys(initialDieTypeQuantities).map((d) => (
              <DieIcon
                key={d}
                notation={d}
                quantity={diceToRoll[d as keyof DieTypeQuantities]}
                increment={incrementDie}
                decrement={decrementDie}
              />
            ))}
          </div>
          <div className={styles.buttonsWrapper}>
            <Button
              className={styles.clearButton}
              data-dd-action-name="Roll Dice Popup > Clear Dice"
              onClick={() => {
                setDiceToRoll(initialDieTypeQuantities);
                clearDiceCallback?.();
              }}
              data-testid="diceClearButton"
              size="smaller"
              disabled={emptyDiceToRoll}
            >
              Reset
            </Button>
            <Button
              className={styles.rollButton}
              data-dd-action-name="Roll Dice Popup > Roll Dice"
              onClick={handleRoll}
              data-testid="diceRollButton"
              size="smaller"
              disabled={emptyDiceToRoll}
              color="secondary"
            >
              Roll
            </Button>
          </div>
          <div className={styles.rollToWrapper}>
            <div className={styles.rollToContainer}>
              <Button
                id={RollTos.Self}
                size="smaller"
                className={clsx([
                  styles.rollToButton,
                  rollTo === RollTos.Self && styles.rollToActive,
                ])}
                data-dd-action-name="Roll Dice Popup > Roll to Self"
                onClick={handleRollTo}
                data-testid="diceRollToSelfButton"
                data-tooltip-id="rollToSelf"
              >
                <User />
              </Button>
              <Tooltip id="rollToSelf">Roll to self</Tooltip>
              <Button
                id={RollTos.All}
                size="smaller"
                className={clsx([
                  styles.rollToButton,
                  rollTo === RollTos.All && styles.rollToActive,
                ])}
                data-dd-action-name="Roll Dice Popup > Roll to Everyone"
                onClick={handleRollTo}
                data-testid="diceRollToAllButton"
                data-tooltip-id="rollToAll"
              >
                <Users />
              </Button>
              <Tooltip id="rollToAll">Roll to everyone</Tooltip>
            </div>
            <span className={styles.rollToLabel}>
              Rolling to{" "}
              {rollTo === RollTos.All ? "everyone" : rollTo?.toLowerCase()}
            </span>
          </div>
        </div>
      </div>
    </DismissableSection>
  );
};
